/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Type-ahead is a behavior pattern which helps the user in the lists and trees
 * (and literally says: "especially those with more than seven options")
 * navigation. The literal description in Wai-Aria documentation is:
 *
 * - Type a character: focus moves to the next item with a name that starts
 * with the typed character.
 *
 * - Type multiple characters in rapid succession:
 * focus moves to the next item with a name that starts with the string of
 * characters typed.
 *
 * It is a little arbitrary what is rapid succession. As I couln't find any
 * documentation about what rapid succession means, I will make some tests in
 * order to determine what is more comfortable to accomplish one situation or
 * the other.
 *
 * To callibrate this function it is suficient to override the frequency
 * parameter, which after some tests I understand is best at 2.5 (keys per
 * second).
 */

import { EventEmitter } from '@apia/util';
import { KeyboardEvent } from 'react';

class WaiTypeAhead extends EventEmitter<{
  multipleKeys: string;
  singleKey: string;
  typeUpdate: string;
}> {
  frequency = 2.5; // Keys per second
  timeout = 0;
  typing = '';

  constructor(props?: {
    onMultipleKeys?: (value: string) => unknown;
    onSingleKey?: (value: string) => unknown;
    onTypeUpdate?: (value: string) => unknown;
  }) {
    super();

    const { onMultipleKeys, onSingleKey, onTypeUpdate } = props ?? {};

    if (onMultipleKeys) this.on('multipleKeys', onMultipleKeys);
    if (onSingleKey) this.on('singleKey', onSingleKey);
    if (onTypeUpdate) this.on('typeUpdate', onTypeUpdate);
  }

  #restoreTimeout() {
    clearTimeout(this.timeout);

    this.timeout = setTimeout(
      this.#shout.bind(this),
      1000 / this.frequency,
    ) as any;
  }

  #shout({ clear } = { clear: true }) {
    if (this.typing.length === 1) {
      this.emit('singleKey', this.typing);
    } else if (this.typing.length > 1) {
      this.emit('multipleKeys', this.typing);
    }
    if (clear) {
      this.typing = '';
    }
  }

  type(ev: KeyboardEvent) {
    if (ev.key.match(/^[\p{L}\d ]$/u)) {
      ev.preventDefault();
      this.typing = this.typing + ev.key;
      this.emit('typeUpdate', this.typing);
      this.#restoreTimeout();
    } else if (ev.code === 'Backspace') {
      ev.preventDefault();
      this.typing = this.typing.slice(0, -1);
      this.emit('typeUpdate', this.typing);
      this.#restoreTimeout();
    }
  }
}

export { WaiTypeAhead };
